import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, CONTACT_INFO } from '../data/mock';
import { CheckCircle2, ArrowRight, Truck, Shield, Headphones, PawPrint, Heart, Award } from 'lucide-react';

// Small reusable section eyebrow header (paw badge above large display heading)
const SectionHeader = ({ eyebrow, title, titleAccent, light = false }) => (
  <div className="text-center mb-12">
    <span className={`inline-flex items-center gap-2 px-6 py-2 mb-5 rounded-full text-xs font-semibold uppercase tracking-[0.25em] font-body ${
      light ? 'bg-white/10 text-white' : 'bg-white text-brand-brown shadow-sm'
    }`}>
      <PawPrint className="w-4 h-4 text-brand-orange" />
      {eyebrow}
    </span>
    <h2 className={`text-4xl sm:text-5xl md:text-6xl font-bold font-display tracking-tight ${light ? 'text-white' : 'text-brand-brown'}`}>
      {title}
      {titleAccent && <span className="text-brand-orange"> {titleAccent}</span>}
    </h2>
  </div>
);

const HomePage = () => {
  const bestsellers = [
    products.find(p => p.slug === 'plastic-dog-carrier'),
    products.find(p => p.slug === 'pet-feeder-water-bowl'),
    products.find(p => p.slug === 'deluxe-bolster-pet-bed'),
    products.find(p => p.slug === 'assorted-feathers-cat-toy'),
    products.find(p => p.slug === 'basic-nylon-dog-collar'),
    products.find(p => p.slug === 'basic-hooded-litter-pan'),
    products.find(p => p.slug === 'orthopedic-dog-bed'),
    products.find(p => p.slug === 'exercise-ball-for-small-pets'),
  ].filter(Boolean);

  const featured = [
    products.find(p => p.slug === 'interactive-cat-toys'),
    products.find(p => p.slug === 'basic-nylon-dog-lead-leash'),
    products.find(p => p.slug === 'round-dog-bed'),
    products.find(p => p.slug === 'living-room-home-for-pets'),
  ].filter(Boolean);

  useEffect(() => {
    document.title = 'Paw Supplies Group | Premium Pet Accessories & Equipment';
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream font-body text-brand-brown">
      <Header />
      <CartDrawer />

      <main>
        {/* ============================================ */}
        {/* HERO SECTION                                   */}
        {/* ============================================ */}
        <section className="relative bg-brand-cream pt-8 pb-20 md:pb-32 overflow-hidden">
          {/* Background overlay image (subtle) */}
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: `url(${siteImages.heroMain})`,
              backgroundSize: 'cover',
              backgroundPosition: 'right center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-cream via-brand-cream/95 to-transparent" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl py-16 md:py-24">
              {/* Eyebrow pill */}
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white text-brand-brown text-xs font-semibold uppercase tracking-[0.3em] shadow-sm mb-6 font-body">
                <PawPrint className="w-4 h-4 text-brand-orange" />
                Premium Pet Care
              </span>

              {/* Big display headline */}
              <h1 className="font-display font-bold text-brand-brown text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight">
                Elevate Your <span className="text-brand-orange">Pet&apos;s</span><br />
                Everyday Life
              </h1>

              <p className="mt-7 text-lg md:text-xl text-brand-brown-light leading-relaxed max-w-xl">
                Premium accessories and equipment for the dogs and cats who deserve the finest &mdash; carefully curated, fast-shipped and pet-parent approved.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 h-14 px-10 rounded-full bg-brand-orange text-white text-base font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition shadow-lg"
                >
                  Shop Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white text-brand-brown text-base font-semibold uppercase tracking-wider font-display hover:bg-brand-paper transition shadow-sm border border-brand-divider"
                >
                  Our Story
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* TWO PILLARS - Convenience + Enrichment        */}
        {/* ============================================ */}
        <section className="bg-white py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Convenience */}
              <div className="bg-brand-cream rounded-[2rem] overflow-hidden group hover:shadow-xl transition-all">
                <div className="h-64 overflow-hidden">
                  <img
                    src={siteImages.featuredBackground}
                    alt="Convenience"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="font-display text-4xl text-brand-brown font-bold mb-3">Convenience</h3>
                  <p className="text-brand-brown-light text-lg mb-7 leading-relaxed">
                    Pet feeder water bowls and round dog beds may help make daily care easier and more efficient &mdash; especially for busy pet parents.
                  </p>
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-brand-orange text-white font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition"
                  >
                    Shop Now
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              {/* Enrichment */}
              <div className="bg-brand-cream rounded-[2rem] overflow-hidden group hover:shadow-xl transition-all">
                <div className="h-64 overflow-hidden">
                  <img
                    src={siteImages.powerBackground}
                    alt="Enrichment"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="font-display text-4xl text-brand-brown font-bold mb-3">Enrichment</h3>
                  <p className="text-brand-brown-light text-lg mb-7 leading-relaxed">
                    Interactive toys and exercise gear may help keep pets engaged &mdash; reducing boredom and promoting a healthy, active lifestyle.
                  </p>
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-brand-orange text-white font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition"
                  >
                    Shop Now
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* CATEGORY MARQUEE STRIP                         */}
        {/* ============================================ */}
        <section className="bg-brand-orange py-7 overflow-hidden border-y-4 border-brand-orange-dark">
          <div className="flex items-center gap-12 whitespace-nowrap animate-[scroll_30s_linear_infinite]">
            {[...Array(3)].map((_, repeatIdx) => (
              <React.Fragment key={repeatIdx}>
                {categories.map((cat) => (
                  <div key={`${repeatIdx}-${cat.id}`} className="flex items-center gap-8 shrink-0">
                    <span className="font-display font-bold text-white text-3xl sm:text-4xl uppercase tracking-wide">
                      {cat.name}
                    </span>
                    <PawPrint className="w-7 h-7 text-white/70" />
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
          <style>{`
            @keyframes scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-33.333%); }
            }
          `}</style>
        </section>

        {/* ============================================ */}
        {/* BESTSELLERS                                    */}
        {/* ============================================ */}
        <section className="bg-brand-cream py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Best In Store"
              title="Bestsellers of"
              titleAccent="The Month"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bestsellers.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-[1.75rem] overflow-hidden flex flex-col group hover:shadow-xl transition-all"
                >
                  <Link to={`/product/${product.slug}`} className="block relative bg-brand-paper">
                    <img
                      className="w-full object-contain h-56 p-6 group-hover:scale-105 transition-transform duration-500"
                      src={product.image}
                      alt={product.name}
                    />
                  </Link>
                  <div className="p-6 text-center">
                    <Link
                      to={`/product/${product.slug}`}
                      className="block font-display text-xl font-bold text-brand-brown mb-2 hover:text-brand-orange line-clamp-2 min-h-[3.5rem]"
                    >
                      {product.name}
                    </Link>
                    <p className="text-brand-orange font-bold text-2xl font-display mb-4">
                      ${product.price.toFixed(2)}
                    </p>
                    <Link
                      to={`/product/${product.slug}`}
                      className="inline-flex items-center justify-center h-11 px-7 rounded-full bg-brand-orange text-white text-sm font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* TAILORED FOR TAILS - Dark brown band           */}
        {/* ============================================ */}
        <section className="relative bg-brand-brown py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: `url(${siteImages.heroLifestyle})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <img
                  className="w-full max-w-lg rounded-[2rem] shadow-2xl object-cover h-[460px]"
                  src={siteImages.heroSecondary}
                  alt="Tailored for Tails"
                />
              </div>
              <div className="order-1 lg:order-2 text-center lg:text-left">
                <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 text-white text-xs font-semibold uppercase tracking-[0.3em] mb-5 font-body backdrop-blur-sm">
                  <PawPrint className="w-4 h-4 text-brand-orange" />
                  Tailored for Tails
                </span>
                <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight tracking-tight">
                  Your pet&apos;s happiness,<br />
                  <span className="text-brand-orange">our priority</span>
                </h2>
                <p className="mt-6 text-lg text-white/80 leading-relaxed">
                  At <strong className="text-white">Paw Supplies Group</strong>, we believe pets deserve the finest. Our carefully curated selection of products may help enhance your pet&apos;s comfort, health and happiness &mdash; and may help your pet feel like a pampered prince or princess.
                </p>
                <div className="mt-9">
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 h-14 px-10 rounded-full bg-brand-orange text-white text-base font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition shadow-lg"
                  >
                    Shop Now
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* 4 icon badges */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
              {[
                { icon: Award, title: 'Ensuring Excellence', desc: 'Superior Quality Products' },
                { icon: Truck, title: 'Swift Delivery', desc: 'Across the U.S.A.' },
                { icon: Shield, title: 'Safe Transactions', desc: 'With 256-Bit Encryption' },
                { icon: Headphones, title: 'Customer Support', desc: 'Via Email & Telephone' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-brand-orange/20 flex items-center justify-center ring-2 ring-brand-orange/40">
                    <item.icon className="w-7 h-7 text-brand-orange" />
                  </div>
                  <p className="font-display font-bold text-white text-lg">{item.title}</p>
                  <p className="text-white/70 text-sm mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* MOST LOVED - Featured Pet Items                */}
        {/* ============================================ */}
        <section className="bg-brand-cream py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Most Loved"
              title="Featured Pet"
              titleAccent="Items"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-[1.75rem] overflow-hidden flex flex-col group hover:shadow-xl transition-all"
                >
                  <Link to={`/product/${product.slug}`} className="block relative bg-brand-paper">
                    <img
                      className="w-full object-contain h-56 p-6 group-hover:scale-105 transition-transform duration-500"
                      src={product.image}
                      alt={product.name}
                    />
                  </Link>
                  <div className="p-6 text-center">
                    <Link
                      to={`/product/${product.slug}`}
                      className="block font-display text-xl font-bold text-brand-brown mb-2 hover:text-brand-orange line-clamp-2 min-h-[3.5rem]"
                    >
                      {product.name}
                    </Link>
                    <p className="text-brand-orange font-bold text-2xl font-display mb-4">
                      ${product.price.toFixed(2)}
                    </p>
                    <Link
                      to={`/product/${product.slug}`}
                      className="inline-flex items-center justify-center h-11 px-7 rounded-full bg-brand-orange text-white text-sm font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* View All */}
            <div className="text-center mt-14">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 h-14 px-10 rounded-full bg-brand-brown text-white text-base font-semibold uppercase tracking-wider font-display hover:bg-brand-brown-dark transition shadow-lg"
              >
                View All Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* FUREVER LOVED - CTA                            */}
        {/* ============================================ */}
        <section className="bg-white py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-brand-cream rounded-[2.5rem] px-6 py-16 md:px-16 md:py-20 relative overflow-hidden">
              {/* Decorative paws */}
              <PawPrint className="absolute top-8 left-8 w-12 h-12 text-brand-orange/15" />
              <PawPrint className="absolute top-16 right-16 w-20 h-20 text-brand-orange/15" />
              <PawPrint className="absolute bottom-12 left-20 w-16 h-16 text-brand-orange/15" />
              <PawPrint className="absolute bottom-8 right-8 w-12 h-12 text-brand-orange/15" />
              <Heart className="absolute top-20 left-1/3 w-10 h-10 text-brand-orange/15" />

              <div className="relative text-center">
                <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-white text-brand-brown text-xs font-semibold uppercase tracking-[0.3em] mb-6 font-body shadow-sm">
                  <PawPrint className="w-4 h-4 text-brand-orange" />
                  Furever Loved
                </span>
                <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-brand-brown leading-tight tracking-tight">
                  Happy Pets, <span className="text-brand-orange">Happy You</span>
                </h2>
                <p className="mt-7 text-lg text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
                  Your pet&apos;s happiness is just a click away. Explore our user-friendly website and discover a world of pet products. Shop with confidence, knowing your furry friend deserves the best.
                </p>
                <div className="mt-10">
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 h-14 px-10 rounded-full bg-brand-orange text-white text-base font-semibold uppercase tracking-wider font-display hover:bg-brand-orange-dark transition shadow-lg"
                  >
                    Shop Now
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* FAQ TEASER                                     */}
        {/* ============================================ */}
        <section className="bg-brand-cream py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader eyebrow="Need Help" title="Frequently Asked" titleAccent="Questions" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {[
                {
                  q: 'What is the typical shipping time?',
                  a: `Your order will be processed and shipped via USPS within 1 business day. Delivery typically takes 2 to 4 business days. For any delay, please contact us at ${CONTACT_INFO.phone}.`
                },
                {
                  q: 'Is shipping free?',
                  a: 'Yes, every order includes free USPS standard shipping within the United States.'
                },
                {
                  q: 'What does the 30-day return policy entail?',
                  a: 'You have 30 days from receiving your product to initiate a return. Please contact customer service for assistance.'
                },
                {
                  q: 'How can I reach customer service?',
                  a: `Our customer care team is available at ${CONTACT_INFO.phone} or via email at ${CONTACT_INFO.email}.`
                },
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl p-7 hover:shadow-md transition flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand-orange flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-brand-brown mb-2">{faq.q}</h3>
                    <p className="text-brand-brown-light text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-brand-orange hover:text-brand-orange-dark font-semibold uppercase tracking-wider text-sm font-display"
              >
                See All FAQs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
